import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { Box, Button, Modal, TextField, Typography, FormControl, Select, MenuItem } from '@mui/material'
import { mainGreen, mainGrey } from '../utils/color'
import AlarmIcon from '@mui/icons-material/Alarm'
import AddPhotoAlternateRoundedIcon from '@mui/icons-material/AddPhotoAlternateRounded'
import CustomInput from './CustomInput'
import { formatPrice } from '../utils/helper'
import SellIcon from '@mui/icons-material/Sell'
import { useSelector } from 'react-redux'
import { DOMAIN } from '../utils/api'
import axiosInstance from '../services/apiBase'
import SelectTagModal from './SelectTagModal'

const formatProductInfo = (order) => {
	if (!order) return ''
	if (['zalo', 'web', 'vizio'].includes(order.source)) {
		return (
			order.line_items
				?.filter((item) => item.quantity != null)
				?.map((item) => `${item.quantity} x ${item.name}`)
				?.join('\n') || ''
		)
	}
	return order.productInfo
}

export default function EditGuestOrderForm({ order, closeForm, submitOrder, storeId }) {
	const initialOrderData = useMemo(
		() => ({
			...order,
			quantity: order.quantity || 0,
			bill: formatPrice(order.bill || 0),
			productInfo: formatProductInfo(order),
			address: order.address || '',
			name: order.name || '',
			phone: order.phone || '',
			payment: order.payment === 'transfer',
			note: order.note || '',
			shippingTotal: formatPrice(order.shippingTotal || 0),
		}),
		[order],
	)

	const { currentUser } = useSelector((state) => state.currentUser)
	const [orderData, setOrderData] = useState(initialOrderData)
	const [fileUpload, setFileUpload] = useState(null)
	const [showTimer, setShowTimer] = useState(false)
	const [preview, setPreview] = useState(null)

	const [openTagModal, setOpenTagModal] = useState(false)
	const [selectedTags, setSelectedTags] = useState([])
	const [tags, setTags] = useState([])

	const tenDateAhead = useMemo(
		() =>
			Array.from({ length: 10 }, (_, i) => {
				const date = new Date()
				date.setDate(date.getDate() + i)
				return date.toLocaleDateString(['en-GB'], {
					timeZone: 'Asia/Saigon',
					day: '2-digit',
					month: '2-digit',
					year: 'numeric',
				})
			}),
		[],
	)

	const [timer, setTimer] = useState({
		hours: '',
		minutes: '',
		date: tenDateAhead[0],
	})

	useEffect(() => {
		if (!fileUpload) {
			setPreview(null)
			return
		}
		const objectUrl = URL.createObjectURL(fileUpload)
		setPreview(objectUrl)
		return () => URL.revokeObjectURL(objectUrl)
	}, [fileUpload])

	const handleChangeTime = useCallback((e) => {
		const { name, value } = e.target
		setTimer((prev) => ({ ...prev, [name]: value }))
	}, [])

	const handleChange = useCallback((e) => {
		const { name, value } = e.target
		if (name === 'bill' || name === 'shippingTotal') {
			const formatedNumber = formatPrice(value)
			setOrderData((prev) => ({
				...prev,
				[name]: formatedNumber,
			}))
		} else {
			setOrderData((prev) => ({
				...prev,
				[name]: value,
			}))
		}
	}, [])

	const tagApiURL = useCallback(() => {
		if (currentUser.role === 'admin' || currentUser.role === 'director' || currentUser.role === 'assistant') {
			return `${DOMAIN}/storeManager/tags/${storeId}`
		} else {
			return `${DOMAIN}/storeManager/tags/${currentUser.store}`
		}
	}, [currentUser.role, currentUser.store, storeId])

	const getTags = useCallback(
		async function () {
			const url = tagApiURL()
			try {
				const response = await axiosInstance({ url, method: 'GET', withCredentials: true })
				setTags(response.data)
			} catch (error) {
				console.log(error.response.data.message)
			}
		},
		[tagApiURL],
	)

	useEffect(() => {
		getTags()
	}, [getTags])

	const handleCustomerInfoChange = useCallback((e) => {
		const [name, phone] = e.target.value.split('-').map((str) => str.trim())
		const addressLine = e.target.value.split('\n')[1]

		setOrderData((prev) => ({
			...prev,
			name: name || prev.name,
			phone: phone || prev.phone,
			address: addressLine || prev.address,
		}))
	}, [])

	const handleSubmit = async (e) => {
		e.preventDefault()
		const formData = {
			...orderData,
			bill: orderData.bill.replace(/,/g, ''),
			shippingTotal: orderData.shippingTotal.replace(/,/g, ''),
			payment: orderData.payment ? 'transfer' : 'cash',
		}

		let scheduleTime = null

		if (showTimer && timer.hours && timer.minutes) {
			scheduleTime = {
				hours: timer.hours,
				minutes: timer.minutes,
				seconds: 0,
				date: timer.date.slice(0, 2),
				month: timer.date.slice(3, 5),
				year: Number(timer.date.slice(6, 11)),
			}
		}

		let image = null

		if (fileUpload) {
			image = fileUpload
		}
		const formattedTags = selectedTags.map((tagName) => ({
			name: tagName,
			color: tags.find((t) => t.name === tagName)?.color || '',
		}))

		await submitOrder(formData, { scheduleTime, image, tags: formattedTags })
	}

	const customerInfoValue = useMemo(
		() => `${orderData.name} - ${orderData.phone}\n${orderData.address}`,
		[orderData.name, orderData.phone, orderData.address],
	)

	const isZaloOrWeb = ['zalo', 'web', 'vizio'].includes(order.source)

	const handleTagClick = () => {
		setOpenTagModal(true)
	}

	return (
		<Modal open={true} onClose={closeForm} sx={styles.modal}>
			<Box sx={styles.modalContent}>
				<Box component='form' onSubmit={handleSubmit} sx={styles.form}>
					{/* Thông tin khách hàng */}
					<Box sx={styles.section}>
						<CustomInput
							multiline
							rows={2}
							value={customerInfoValue}
							name='customerInfo'
							placeholder='Thông tin khách hàng'
							onChange={handleCustomerInfoChange}
							sx={styles.customerInfo}
						/>
					</Box>

					{/* Tiền và phí ship */}
					<Box sx={styles.section}>
						<Box sx={styles.inputGroup}>
							<CustomInput value={orderData.bill} name='bill' placeholder='Tiền (vnđ)' onChange={handleChange} />
							<CustomInput
								value={orderData.shippingTotal}
								name='shippingTotal'
								placeholder='Phí giao hàng'
								onChange={handleChange}
							/>
						</Box>
					</Box>

					{/* Chi tiết sản phẩm */}
					<Box sx={styles.section}>
						<CustomInput
							multiline
							rows={5}
							value={orderData.productInfo}
							name='productInfo'
							placeholder='Chi tiết sản phẩm'
							onChange={handleChange}
							disabled={isZaloOrWeb}
							sx={styles.productInfo}
						/>
					</Box>

					{/* Ghi chú */}
					<Box sx={styles.section}>
						<CustomInput
							multiline
							rows={3}
							value={orderData.note}
							name='note'
							placeholder='Ghi chú (nếu có)'
							onChange={handleChange}
							sx={styles.note}
						/>
					</Box>

					{/* Actions */}
					<Box sx={styles.actions}>
						<AlarmIcon
							onClick={() => setShowTimer(!showTimer)}
							sx={{
								...styles.actionIcon,
								color: showTimer ? mainGreen : '#666',
							}}
						/>
						<label htmlFor='upload-photo'>
							<AddPhotoAlternateRoundedIcon sx={styles.actionIcon} />
						</label>
						<input
							id='upload-photo'
							type='file'
							accept='image/*'
							onChange={(e) => setFileUpload(e.target.files[0])}
							style={{ display: 'none' }}
						/>
						<Button
							variant='outlined'
							onClick={() => setOrderData((prev) => ({ ...prev, payment: !prev.payment }))}
							sx={{
								...styles.actionButton,
								bgcolor: orderData.payment ? mainGreen : 'white',
								color: orderData.payment ? 'white' : '#333',
							}}>
							Chuyển khoản
						</Button>
						<Button variant='outlined' onClick={handleTagClick} sx={styles.tagButton}>
							<SellIcon sx={{ color: '#f44336', transform: 'rotate(45deg)' }} />
						</Button>
					</Box>

					{/* Timer */}
					{showTimer && (
						<Box sx={styles.timer}>
							<Box sx={styles.timeInputs}>
								<TextField
									size='small'
									placeholder='00'
									value={timer.hours}
									name='hours'
									onChange={handleChangeTime}
									inputProps={{ min: 0, max: 23, type: 'number' }}
									sx={styles.timeField}
								/>
								<Typography>:</Typography>
								<TextField
									size='small'
									placeholder='00'
									value={timer.minutes}
									name='minutes'
									onChange={handleChangeTime}
									inputProps={{ min: 0, max: 59, type: 'number' }}
									sx={styles.timeField}
								/>
							</Box>
							<FormControl size='small' sx={styles.dateSelect}>
								<Select value={timer.date} name='date' onChange={handleChangeTime}>
									{tenDateAhead.map((date, index) => (
										<MenuItem key={index} value={date}>
											{date.slice(0, 5)}
										</MenuItem>
									))}
								</Select>
							</FormControl>
						</Box>
					)}

					{/* Preview Image */}
					{preview && (
						<Box sx={styles.previewContainer}>
							<img src={preview} alt='preview' style={styles.previewImage} />
							<Button onClick={() => setFileUpload(null)} variant='outlined' sx={styles.removeImageBtn}>
								Xóa ảnh
							</Button>
						</Box>
					)}

					{/* Submit Button */}
					<Button type='submit' variant='contained' fullWidth sx={styles.submitButton}>
						Cập nhật đơn hàng
					</Button>

					{/* Add TagModal */}
					<SelectTagModal
						open={openTagModal}
						onClose={() => setOpenTagModal(false)}
						tags={tags}
						selectedTags={selectedTags}
						onTagsChange={setSelectedTags}
					/>
				</Box>
			</Box>
		</Modal>
	)
}

const styles = {
	modal: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	modalContent: {
		background: mainGrey,
		border: `2px solid ${mainGreen}`,
		borderRadius: '1rem',
		boxShadow: 'rgba(9, 30, 66, 0.25) 0px 4px 8px -2px, rgba(9, 30, 66, 0.08) 0px 0px 0px 1px',
		width: '800px',
		maxWidth: '100%',
		maxHeight: '80vh',
		overflowY: 'auto',
		position: 'relative',
		mx: 2,
	},
	form: {
		p: 2,
	},
	inputGroup: {
		display: 'grid',
		gridTemplateColumns: '1fr 1fr',
		gap: 2,
		mb: 2,
	},
	textAreaGroup: {
		display: 'flex',
		flexDirection: 'column',
		gap: 2,
		mb: 2,
	},
	actions: {
		display: 'flex',
		alignItems: 'center',
		gap: 2,
		mb: 2,
	},
	actionButton: {
		textTransform: 'none',
		borderRadius: '20px',
		border: '1px solid #ccc',
		'&:hover': {
			border: '1px solid #999',
		},
	},
	timerIcon: {
		cursor: 'pointer',
	},
	uploadIcon: {
		cursor: 'pointer',
		color: '#666',
	},
	timer: {
		bgcolor: 'white',
		p: 1.5,
		borderRadius: 2,
		display: 'flex',
		alignItems: 'center',
		gap: 2,
		mb: 2,
	},
	timeInputs: {
		display: 'flex',
		alignItems: 'center',
		gap: 1,
	},
	timeField: {
		width: 60,
	},
	dateSelect: {
		minWidth: 100,
	},
	previewContainer: {
		display: 'flex',
		alignItems: 'center',
		gap: 2,
		mb: 2,
	},
	previewImage: {
		width: 50,
		height: 50,
		borderRadius: '4px',
		objectFit: 'cover',
	},
	removeImageBtn: {
		textTransform: 'none',
		borderColor: '#ff4444',
		color: '#ff4444',
		'&:hover': {
			borderColor: '#cc0000',
			backgroundColor: 'rgba(255,68,68,0.1)',
		},
	},
	submitButton: {
		bgcolor: mainGreen,
		textTransform: 'none',
		borderRadius: '30px',
		py: 1.5,
		fontSize: '16px',
		'&:hover': {
			bgcolor: '#008d4c',
		},
	},
	modalTitle: {
		p: 2,
		borderBottom: '1px solid #eee',
		fontWeight: 600,
	},
	section: {
		mb: 2,
	},
	customerInfo: {
		'& .MuiInputBase-root': {
			backgroundColor: 'white',
		},
	},
	productInfo: {
		'& .MuiInputBase-root': {
			backgroundColor: 'white',
		},
	},
	note: {
		'& .MuiInputBase-root': {
			backgroundColor: 'white',
		},
	},
	rightActions: {
		display: 'flex',
		alignItems: 'center',
		gap: 2,
		marginLeft: 'auto',
	},
	actionIcon: {
		cursor: 'pointer',
		color: '#666',
		'&:hover': {
			color: mainGreen,
		},
	},
	tagButton: {
		textTransform: 'none',
		borderRadius: '20px',
		border: '1px solid #ccc',
		color: '#f44336',
		bgcolor: 'white',
		minWidth: 'unset',
		padding: '6px 16px',
		height: '44px',
		'&:hover': {
			border: '1px solid #999',
			bgcolor: 'rgba(0,0,0,0.05)',
		},
	},
}

